// src/CartContext.js
import React, { createContext, useState, useEffect } from 'react';
import api from './api';

export const CartContext = createContext();

export const CartProvider = ({ children, user }) => {
  const [cartItems, setCartItems] = useState([]);

  /**
   * Fetch the user's cart from the server and ensure we ONLY keep valid items
   * (i.e., entries where productId still exists). This prevents the badge
   * from counting ghost items when products are deleted/disabled.
   */
  const refreshCart = async () => {
    try {
      if (!user?.username) return;

      const res = await api.get(`/cart/${user.username}`);
      const items = res.data?.cart?.items || [];

      // ✅ Drop items with missing/null productId (deleted or unavailable products)
      const validItems = items.filter((item) => item && item.productId);
      setCartItems(validItems);
    } catch (err) {
      console.error('🛒 Failed to refresh cart:', err);
    }
  };

  // Load cart on mount / when username changes
  useEffect(() => {
    refreshCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  /**
   * Add to cart, then refresh (server is source of truth).
   */
  const addToCart = async (productId) => {
    if (!user?.username) return;
    try {
      await api.post('/cart/add', {
        username: user.username,
        productId,
      });
      await refreshCart(); // ✅ live update
    } catch (err) {
      console.error('❌ Failed to add to cart:', err);
      throw err; // allow UI to handle errors
    }
  };

  /**
   * Remove from cart, then refresh.
   */
  const removeFromCart = async (productId) => {
    if (!user?.username) return;
    try {
      await api.post('/cart/remove', {
        username: user.username,
        productId,
      });
      await refreshCart(); // ✅ live update
    } catch (err) {
      console.error('❌ Failed to remove from cart:', err);
      throw err; // allow UI to handle errors
    }
  };

  /**
   * Optional helper: when a screen (e.g., CartOverview) already fetched the
   * server cart and knows which productIds are valid, call this to prune the
   * context immediately so the badge updates without an extra network call.
   */
  const syncWithServer = (validProductIds = []) => {
    const validSet = new Set(validProductIds.map(String));
    setCartItems((prev) =>
      prev.filter((i) => {
        const id = i?.productId?._id || i?.productId;
        return id && validSet.has(String(id));
      })
    );
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        refreshCart,
        syncWithServer, // 👈 used by CartOverview (next step)
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => React.useContext(CartContext);
